import { useTenantCurrency, getCurrencySymbol } from '@/hooks/use-tenant-currency';
import { useTaxRegime } from '@/hooks/use-tax-regime';
import { getCurrencyDecimals, getSubunitMultiplier } from '@bbpos/utils';
import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { PageContainer, Button, Skeleton, Modal, Input, Select, ErrorBoundary, useToast } from '@bbpos/ui';
import { formatCurrency } from '@/utils/currency';
import { GLASS, GLASS_FORM } from '@/constants/design-tokens';
import {
  useSalesInvoice, useRecordSalesInvoicePayment, useCancelSalesInvoice, useDownloadSalesInvoice, SalesInvoiceStatusBadge,
} from '@/features/sales-invoices';
import type { SalesPaymentMethod } from '@/features/sales-invoices';
import { useAuthStore } from '@/stores/use-auth-store';
import { ROLE_LEVELS } from '@/types/user';
import { toLocalIsoDate } from '../purchase-orders/date-utils';
import { BanIcon, CheckIcon, DownloadIcon } from '../purchase-orders/icons';

const METHOD_OPTIONS: { value: SalesPaymentMethod; label: string }[] = [
  { value: 'BANK_TRANSFER', label: 'Bank transfer' },
  { value: 'UPI', label: 'UPI' },
  { value: 'CASH', label: 'Cash' },
  { value: 'CHEQUE', label: 'Cheque' },
  { value: 'CARD', label: 'Card' },
];

function fmtDate(v: string | null | undefined) {
  return v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
}

function methodLabel(m: string): string {
  return METHOD_OPTIONS.find((o) => o.value === m)?.label ?? m;
}

function SalesInvoiceDetailContent() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const currency = useTenantCurrency();
  const tax = useTaxRegime();
  const subunit = getSubunitMultiplier(currency);
  const dp = getCurrencyDecimals(currency);
  const profile = useAuthStore((s) => s.profile);
  const isManager = (ROLE_LEVELS[profile?.role ?? ''] ?? 0) >= ROLE_LEVELS.MANAGER;

  const { data: inv, isLoading, error } = useSalesInvoice(id);
  const payMutation = useRecordSalesInvoicePayment();
  const cancelMutation = useCancelSalesInvoice();
  const pdfMutation = useDownloadSalesInvoice();

  const [showPay, setShowPay] = useState(false);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState<SalesPaymentMethod>('BANK_TRANSFER');
  const [payDate, setPayDate] = useState(toLocalIsoDate(new Date()));
  const [payRef, setPayRef] = useState('');
  const [payNotes, setPayNotes] = useState('');
  const [showCancel, setShowCancel] = useState(false);
  const [cancelReason, setCancelReason] = useState('');

  // getById maps a 404 to null, so a missing invoice arrives as data, not as an error.
  const failure = error ? (error as Error).message : inv === null ? 'Sales invoice not found' : null;
  useEffect(() => {
    if (!failure) return;
    toast({ title: failure, variant: 'error' });
    navigate('/sales-invoices');
  }, [failure, toast, navigate]);

  if (failure) return null;
  if (isLoading || !inv) return (<><Skeleton variant="rect" height={96} className="rounded-xl mb-6" /><Skeleton variant="rect" height={320} className="rounded-xl" /></>);

  const live = inv.status !== 'CANCELLED';
  const payable = (inv.status === 'OPEN' || inv.status === 'PARTIALLY_PAID') && isManager;
  // Only money paid against the invoice blocks cancellation; an applied advance simply returns to the order.
  const cancellable = live && inv.amount_paid === 0 && isManager;
  const overdue = live && inv.due_date && inv.balance_due > 0 && inv.due_date.slice(0, 10) < toLocalIsoDate(new Date());

  function handleDownloadPdf() {
    if (!inv) return;
    pdfMutation.mutate(inv.id, {
      onSuccess: ({ blob, filename }) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = filename ?? `${inv.invoice_number}.pdf`;
        document.body.appendChild(a); a.click(); document.body.removeChild(a);
        URL.revokeObjectURL(url);
      },
      onError: (e: Error) => toast({ title: e.message || 'Failed to download the invoice', variant: 'error' }),
    });
  }
  function openPay() {
    setPayAmount((inv!.balance_due / subunit).toFixed(dp)); setPayMethod('BANK_TRANSFER'); setPayDate(toLocalIsoDate(new Date())); setPayRef(''); setPayNotes(''); setShowPay(true);
  }
  function handlePay() {
    const amount = Math.round((parseFloat(payAmount) || 0) * subunit);
    if (amount <= 0 || amount > inv!.balance_due) { toast({ title: `Amount must be between ${formatCurrency(1, currency)} and ${formatCurrency(inv!.balance_due, currency)}`, variant: 'error' }); return; }
    payMutation.mutate(
      { soId: inv!.sales_order_id, invoiceId: inv!.id, payload: { amount, payment_method: payMethod, paid_on: payDate || undefined, reference: payRef.trim() || undefined, notes: payNotes.trim() || undefined } },
      {
        onSuccess: (p) => {
          setShowPay(false);
          const remaining = inv!.balance_due - p.amount;
          toast({ title: remaining <= 0 ? `${inv!.invoice_number} fully paid` : `Payment recorded — ${formatCurrency(remaining, currency)} still due`, variant: 'success' });
        },
        onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
      },
    );
  }
  function handleCancel() {
    cancelMutation.mutate({ soId: inv!.sales_order_id, invoiceId: inv!.id, reason: cancelReason.trim() || undefined }, {
      onSuccess: () => { setShowCancel(false); toast({ title: `${inv!.invoice_number} cancelled`, variant: 'success' }); },
      onError: (e: Error) => toast({ title: e.message, variant: 'error' }),
    });
  }

  return (
    <>
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-teal-500 via-success-500 to-brand-600 p-6 text-white shadow-[0_24px_60px_-24px_rgba(20,184,166,0.55)] mb-6 animate-fade-in">
        <div className="pointer-events-none absolute -top-24 -right-24 h-56 w-56 rounded-full bg-white/20 blur-3xl" />
        <div className="relative flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex items-center gap-3"><h1 className="text-2xl font-bold tracking-tight font-mono">{inv.invoice_number}</h1><SalesInvoiceStatusBadge status={inv.status} /></div>
            <p className="text-teal-50 mt-1">{inv.customer_name} · against{' '}
              <button type="button" onClick={() => navigate(`/sales-orders/${inv.sales_order_id}`)} className="underline font-mono">{inv.so_number}</button>
              <span> · PO {inv.customer_po_number}</span>
            </p>
          </div>
          <div className="text-right">
            <p className="text-3xl font-bold tabular-nums font-mono">{formatCurrency(live ? inv.balance_due : 0, currency)}</p>
            <p className="text-xs uppercase tracking-wide text-teal-50">
              Balance due{overdue ? ' · overdue' : ''} · total {formatCurrency(inv.total_amount, currency)}{inv.advance_applied > 0 ? ` · advance ${formatCurrency(inv.advance_applied, currency)}` : ''}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Invoice Information</h3>
            <dl className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Invoice date</dt><dd>{fmtDate(inv.invoice_date)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Due date</dt><dd className={overdue ? 'text-danger-600 font-medium' : ''}>{fmtDate(inv.due_date)}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Sales order</dt><dd><button type="button" onClick={() => navigate(`/sales-orders/${inv.sales_order_id}`)} className="font-mono text-brand-600 dark:text-brand-400 hover:underline">{inv.so_number}</button><span className="text-xs text-neutral-500"> · {inv.sales_order_status.replace('_', ' ').toLowerCase()}</span></dd></div>
              <div>
                <dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Customer</dt>
                <dd>
                  <div className="font-medium text-neutral-800 dark:text-neutral-200">{inv.customer_name}</div>
                  {inv.customer_phone && <div className="text-xs text-neutral-500 tabular-nums">{inv.customer_phone}</div>}
                  {inv.customer_gstin && <div className="text-xs text-neutral-500 font-mono">{tax.taxIdLabel} {inv.customer_gstin}</div>}
                </dd>
              </div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Customer PO</dt><dd className="font-mono">{inv.customer_po_number}{inv.customer_po_date ? <span className="text-neutral-500 font-sans"> · {fmtDate(inv.customer_po_date)}</span> : null}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Location</dt><dd>{inv.location_name ?? '—'}</dd></div>
              <div><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Created by</dt><dd>{inv.created_by_name ?? '—'} · {fmtDate(inv.created_at)}</dd></div>
              {inv.billing_address && <div className="col-span-full"><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Billing address</dt><dd className="whitespace-pre-wrap">{inv.billing_address}</dd></div>}
              {inv.notes && <div className="col-span-full"><dt className="text-xs uppercase tracking-wide text-neutral-500 mb-1">Notes</dt><dd>{inv.notes}</dd></div>}
              {inv.status === 'CANCELLED' && <div className="col-span-full"><dt className="text-xs uppercase tracking-wide text-danger-600 mb-1">Cancelled</dt><dd>{inv.cancel_reason ?? 'No reason given'} · {fmtDate(inv.cancelled_at)}</dd></div>}
            </dl>
          </div>

          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-4">Lines ({inv.items.length})</h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-neutral-100 dark:border-neutral-800">
                    <th className="text-left py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Item</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Qty</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Unit price</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Discount</th>
                    <th className="text-right py-2 pr-3 text-xs font-medium text-neutral-500 uppercase tracking-wide">Tax</th>
                    <th className="text-right py-2 text-xs font-medium text-neutral-500 uppercase tracking-wide">Total</th>
                  </tr>
                </thead>
                <tbody>
                  {inv.items.map((i) => (
                    <tr key={i.id} className="border-b border-neutral-50 dark:border-neutral-800/50 last:border-0 align-top even:bg-neutral-50/60 dark:even:bg-neutral-800/30 hover:bg-teal-50/60 dark:hover:bg-teal-900/10 transition-colors">
                      <td className="py-2.5 pr-3">
                        <div className="font-medium text-neutral-800 dark:text-neutral-200">{i.item_name}</div>
                        {i.hsn_code && <div className="text-[11px] text-neutral-500 font-mono">HSN {i.hsn_code}</div>}
                      </td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{i.quantity}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{formatCurrency(i.unit_price, currency)}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{i.line_discount > 0 ? `−${formatCurrency(i.line_discount, currency)}` : '—'}</td>
                      <td className="py-2.5 pr-3 text-right tabular-nums">{i.line_tax > 0 ? <>{formatCurrency(i.line_tax, currency)} <span className="text-xs text-neutral-500">({i.gst_rate}%)</span></> : '—'}</td>
                      <td className="py-2.5 text-right font-semibold tabular-nums">{formatCurrency(i.line_total, currency)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 pt-4 border-t border-neutral-100 dark:border-neutral-800 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-neutral-500">Subtotal</span><span className="tabular-nums">{formatCurrency(inv.subtotal, currency)}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Discount</span><span className="tabular-nums text-success-600">{inv.discount_total > 0 ? `−${formatCurrency(inv.discount_total, currency)}` : '—'}</span></div>
              <div className="flex justify-between"><span className="text-neutral-500">Tax</span><span className="tabular-nums">{inv.tax_total > 0 ? formatCurrency(inv.tax_total, currency) : '—'}</span></div>
              {/* T07: cash rounding chosen on the invoice, already in the total. */}
              {(inv.rounding_adjustment ?? 0) !== 0 && <div className="flex justify-between"><span className="text-neutral-500">Rounding</span><span className="tabular-nums">{(inv.rounding_adjustment ?? 0) < 0 ? `−${formatCurrency(-(inv.rounding_adjustment ?? 0), currency)}` : formatCurrency(inv.rounding_adjustment ?? 0, currency)}</span></div>}
              <div className="flex justify-between font-bold pt-2 border-t border-neutral-100 dark:border-neutral-800"><span>Total</span><span className="tabular-nums">{formatCurrency(inv.total_amount, currency)}</span></div>
              {inv.advance_applied > 0 && <div className="flex justify-between"><span className="text-neutral-500">Advance applied</span><span className="tabular-nums text-violet-600 dark:text-violet-300">−{formatCurrency(inv.advance_applied, currency)}</span></div>}
              <div className="flex justify-between"><span className="text-neutral-500">Paid</span><span className="tabular-nums text-success-600">{formatCurrency(inv.amount_paid, currency)}</span></div>
              <div className="flex justify-between font-semibold"><span>Balance due</span><span className={`tabular-nums ${live && inv.balance_due > 0 ? 'text-warning-700 dark:text-warning-300' : ''}`}>{formatCurrency(live ? inv.balance_due : 0, currency)}</span></div>
            </div>
          </div>

          <div className={`${GLASS} p-6 animate-slide-up`}>
            <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Payments ({inv.payments.length})</h3>
            {inv.payments.length === 0 ? <p className="text-sm text-neutral-500">No payments recorded against this invoice yet.</p> : (
              <ul className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {inv.payments.map((p) => (
                  <li key={p.id} className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm">
                    <span className="tabular-nums text-neutral-600 dark:text-neutral-400">{fmtDate(p.paid_on)}</span>
                    <span className="text-neutral-700 dark:text-neutral-300">{methodLabel(p.payment_method)}{p.reference ? ` · ${p.reference}` : ''}</span>
                    <span className="text-xs text-neutral-500">{p.created_by_name ?? ''}</span>
                    <span className="font-semibold tabular-nums">{formatCurrency(p.amount, currency)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>

        <div className="space-y-6">
          {isManager && (
            <div className={`${GLASS_FORM} p-5 animate-slide-up`}>
              <h3 className="text-sm font-semibold text-neutral-700 dark:text-neutral-200 mb-3">Actions</h3>
              <div className="space-y-2">
                {payable && <Button variant="primary" className="w-full" onClick={openPay}><CheckIcon className="h-4 w-4 mr-1.5" />Record Payment</Button>}
                <Button variant="secondary" className="w-full" onClick={handleDownloadPdf} loading={pdfMutation.isPending}><DownloadIcon className="h-4 w-4 mr-1.5" />Download Invoice</Button>
                <Button variant="secondary" className="w-full" onClick={() => navigate(`/sales-orders/${inv.sales_order_id}`)}>Open {inv.so_number}</Button>
                {cancellable && <Button variant="danger" className="w-full" onClick={() => { setCancelReason(''); setShowCancel(true); }}><BanIcon className="h-4 w-4 mr-1.5" />Cancel Invoice</Button>}
              </div>
            </div>
          )}
        </div>
      </div>

      <Modal open={showPay} onClose={() => setShowPay(false)} title={`Record payment · ${inv.invoice_number}`} allowOverflow
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowPay(false)}>Cancel</Button><Button variant="primary" onClick={handlePay} loading={payMutation.isPending}>Save payment</Button></div>}>
        <div className="grid grid-cols-2 gap-3">
          <div><label className="block text-sm font-medium mb-1">Amount ({getCurrencySymbol(currency)})</label><Input type="number" value={payAmount} onChange={setPayAmount} /><p className="text-xs text-neutral-500 mt-1">Balance due {formatCurrency(inv.balance_due, currency)}</p></div>
          <div><label className="block text-sm font-medium mb-1">Method</label><Select options={METHOD_OPTIONS} value={payMethod} onChange={(v) => setPayMethod((typeof v === 'string' ? v : 'BANK_TRANSFER') as SalesPaymentMethod)} /></div>
          <div><label className="block text-sm font-medium mb-1">Received on</label><Input type="date" value={payDate} onChange={setPayDate} /></div>
          <div><label className="block text-sm font-medium mb-1">Reference</label><Input value={payRef} onChange={setPayRef} placeholder="UTR / cheque no." /></div>
          <div className="col-span-2"><label className="block text-sm font-medium mb-1">Notes</label><Input value={payNotes} onChange={setPayNotes} placeholder="Optional" /></div>
        </div>
      </Modal>

      <Modal open={showCancel} onClose={() => setShowCancel(false)} title={`Cancel ${inv.invoice_number}?`}
        footer={<div className="flex justify-end gap-2"><Button variant="secondary" onClick={() => setShowCancel(false)}>Keep</Button>
          <Button variant="danger" loading={cancelMutation.isPending} onClick={handleCancel}>Cancel invoice</Button></div>}>
        <p className="text-sm text-neutral-700 dark:text-neutral-300 mb-3">
          Its quantities return to {inv.so_number}'s un-invoiced balance so a corrected invoice can be raised, and any advance applied to it returns to the order. This cannot be undone.
        </p>
        <label className="block text-sm font-medium mb-1">Reason <span className="text-neutral-400 font-normal">(optional)</span></label>
        <Input value={cancelReason} onChange={setCancelReason} placeholder="Why is this invoice being cancelled?" />
      </Modal>
    </>
  );
}

export function SalesInvoiceDetailPage() {
  return (
    <PageContainer title="Sales Invoice" breadcrumbs={[{ label: 'Finance' }, { label: 'Sales Invoices', href: '/sales-invoices' }, { label: 'Detail' }]}>
      <ErrorBoundary><SalesInvoiceDetailContent /></ErrorBoundary>
    </PageContainer>
  );
}
